// Field validation rules shared by every form. The UI uses them for inline messages; the mock backend applies
// the very same rules server-side, so a form can never save something the browser would have refused. The Python
// backend must enforce these too (see docs/API_CONTRACT.md, "Validation").
//
// Each helper returns an error message string, or "" when the value is fine.

export const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;
const URL_RE = /^https?:\/\/[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}(:\d{2,5})?(\/\S*)?$/;
const HEX_RE = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

const str = (v) => (v === undefined || v === null ? "" : String(v));
const trimmed = (v) => str(v).trim();

export function emailError(value, { required = true } = {}) {
  const v = trimmed(value);
  if (!v) return required ? "Email address is required." : "";
  if (v.length > 254 || !EMAIL_RE.test(v)) return "Enter a valid email address (for example name@company.com).";
  return "";
}

/**
 * Phone: digits with optional spaces, dashes, dots and brackets.
 * National form: 10 digits (11 with a leading trunk 0 or 1). International form: "+" then country code + number (11–13 digits).
 */
export function phoneError(value, { required = false } = {}) {
  const v = trimmed(value);
  if (!v) return required ? "Phone number is required." : "";
  if (!/^\+?[\d\s().-]+$/.test(v) || v.indexOf("+") > 0) return "Phone number can only contain digits, spaces, + ( ) - and .";
  const digits = v.replace(/\D/g, "");
  const intl = v.startsWith("+");
  const ok = intl ? digits.length >= 11 && digits.length <= 13 : digits.length === 10 || (digits.length === 11 && /^[01]/.test(digits));
  if (!ok) return intl ? "Enter a valid phone number: + country code followed by the number (11–13 digits)." : "Enter a valid 10-digit phone number, or use + and the country code.";
  return "";
}

/** Exactly-10-digit national number (Business Profile). */
export function tenDigitPhoneError(value, { required = false } = {}) {
  const v = trimmed(value);
  if (!v) return required ? "Phone number is required." : "";
  return /^[0-9]{10}$/.test(v) ? "" : "Enter a valid 10-digit phone number.";
}

export function urlError(value, { required = false, label = "URL" } = {}) {
  const v = trimmed(value);
  if (!v) return required ? `${label} is required.` : "";
  if (v.length > 2048 || !URL_RE.test(v)) return `Enter a valid ${label} starting with http:// or https://.`;
  return "";
}

export function lengthError(value, { min = 0, max = 255, label = "This field", required = false } = {}) {
  const v = trimmed(value);
  if (!v) return required || min > 0 ? `${label} is required.` : "";
  if (v.length < min) return `${label} must be at least ${min} characters.`;
  if (v.length > max) return `${label} must be at most ${max} characters.`;
  return "";
}

/** A person's name: 2–80 chars with at least one letter (no digits-only / symbol-only names). */
export function nameError(value, { label = "Name", max = 80 } = {}) {
  const v = trimmed(value);
  if (!v) return `${label} is required.`;
  if (v.length < 2) return `${label} must be at least 2 characters.`;
  if (v.length > max) return `${label} must be at most ${max} characters.`;
  if (!/\p{L}/u.test(v)) return `${label} must contain letters.`;
  if (/[<>{}\\[\]]/.test(v)) return `${label} contains characters that aren't allowed.`;
  return "";
}

export function intRangeError(value, { min, max, label = "Value", required = true } = {}) {
  if (value === undefined || value === null || str(value).trim() === "") return required ? `${label} is required.` : "";
  const n = Number(value);
  if (!Number.isInteger(n)) return `${label} must be a whole number.`;
  if (n < min || n > max) return `${label} must be between ${min} and ${max}.`;
  return "";
}

export function hexError(value, label = "Color") {
  return HEX_RE.test(trimmed(value)) ? "" : `${label} must be a hex color like #007BFF.`;
}

export function oneOfError(value, options, label = "Value") {
  return options.includes(value) ? "" : `${label} must be one of: ${options.join(", ")}.`;
}

export function passwordError(value) {
  const v = str(value);
  if (!v) return "Password is required.";
  if (v.length < 8) return "Password must be at least 8 characters.";
  if (v.length > 128) return "Password must be at most 128 characters.";
  if (!/[A-Za-z]/.test(v) || !/\d/.test(v)) return "Password must include at least one letter and one number.";
  return "";
}

/** Collects non-empty messages into a fields map; returns null when everything is valid. */
export function collect(entries) {
  const fields = {};
  entries.forEach(([key, message]) => { if (message && !fields[key]) fields[key] = message; });
  return Object.keys(fields).length ? fields : null;
}

export const firstMessage = (fields) => Object.values(fields)[0];

export const LEAD_STATUSES = ["New", "Contacted", "Qualified", "Won", "Lost"];

/** Lead form rules (add lead, edit lead, visitor capture). `partial` = only check keys that are present. */
export function leadFields(b, partial = false) {
  const has = (k) => !partial || b[k] !== undefined;
  return collect([
    has("name") && ["name", nameError(b.name)],
    has("email") && ["email", emailError(b.email)],
    ["phone", phoneError(b.phone)],
    b.source !== undefined && ["source", lengthError(b.source, { max: 60, label: "Source" })],
    b.intent !== undefined && ["intent", lengthError(b.intent, { max: 100, label: "Intent" })],
    b.company !== undefined && ["company", lengthError(b.company, { max: 80, label: "Company" })],
    b.message !== undefined && ["message", lengthError(b.message, { max: 500, label: "Message" })],
    b.score !== undefined && ["score", intRangeError(b.score, { min: 0, max: 100, label: "Score" })],
    b.status !== undefined && ["status", oneOfError(b.status, LEAD_STATUSES, "Status")]
  ].filter(Boolean));
}

const SAFE_EVENT = /^[A-Za-z0-9_-]+$/;
export function eventNameError(value, label = "Event name") {
  const v = trimmed(value);
  if (!v) return `${label} is required.`;
  if (v.length > 60) return `${label} must be at most 60 characters.`;
  return SAFE_EVENT.test(v) ? "" : `${label} may contain only letters, numbers, underscores and hyphens.`;
}

/** A link target: an in-site path (/pricing) or a full http(s) URL. */
export function targetError(value) {
  const t = trimmed(value);
  if (!t) return "Destination is required.";
  if (t.startsWith("/")) return /^\/[\w\-./?=&%#]*$/.test(t) ? "" : "Enter a valid path like /pricing.";
  return urlError(t, { label: "Destination" });
}

const DOMAIN_RE = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i;
export function domainError(value, { required = true, label = "Business domain" } = {}) {
  const v = trimmed(value);
  if (!v) return required ? `${label} is required.` : "";
  return DOMAIN_RE.test(v) ? "" : "Enter a valid domain, e.g. example.com (no http:// or paths).";
}
